/**
 * Admin 「외부 연동」 — K-MRS 약사 접근코드 API (PhAI 백엔드가 돌봄 관리 API로 중계).
 *
 * @spec:2026-10-15-KMRS-접근코드-PhAI-Admin-관리-전체기관 — D9·D10·D12, 화면 설계 A
 *
 * 경로·요청·응답 타입은 D10 중계 계약 그대로다(PhAI 백엔드가 같은 계약으로 동시에 구현 중).
 * 응답 래퍼·언랩·오류 변환은 외부 연동 선례(`partnerAdminService`)를 재사용한다 — `unwrap`·`call`·
 * `toPartnerAdminError`·`PartnerAdminError`를 import해 쓴다(서비스 계층 import는 ARCHITECTURE §1 대상이
 * 아니다, 선례 `adminSubscriptionService.ts`).
 *
 * K-MRS만의 두 갈래를 더 처리한다(그래서 `call`을 그대로 쓰지 않고 `kmrsCall`로 감싼다):
 *  - 404인데 본문에 `error` 필드가 없음(옛 PhAI 백엔드 — Nest 기본 404) → 「아직 준비 중인 기능이에요」(D12)
 *  - 발급·폐기의 클라이언트 타임아웃(ECONNABORTED) → D10 타임아웃 행과 같은 글자(조회는 선례 범용 문구)
 */
import type { AxiosResponse } from "axios";
import axiosInstance from "./axiosInstance";
import {
  PartnerAdminError,
  toPartnerAdminError,
  unwrap,
  type Envelope,
} from "./partnerAdminService";

const BASE = "/api/v1/admin/kmrs";

/**
 * 발급·폐기 타임아웃 15초. PhAI 중계는 돌봄 호출에 10초를 쓰므로, 전역 10초로는 중계 응답(504 문구)이
 * 오기 전에 끊길 수 있다. 조회는 전역 10초를 그대로 쓴다.
 */
const MUTATION_TIMEOUT_MS = 15000;

/** 옛 PhAI 백엔드(라우트 없음) — 404에 `error` 필드가 없을 때(D12). */
export const NOT_READY_TEXT = "아직 준비 중인 기능이에요";

/**
 * 발급·폐기 클라이언트 타임아웃 문구 — D10 타임아웃 행과 같은 글자.
 * 중계가 살아 있으면 504 본문 message로 같은 글자가 오고, 그보다 늦게 클라이언트가 끊기면 이 문구를 쓴다.
 */
export const ISSUE_TIMEOUT_TEXT =
  "발급됐는지 확인하지 못했어요. 목록을 새로 고쳐 방금 시각·같은 이름표의 코드가 있으면 폐기하고 다시 발급하세요";
export const REVOKE_TIMEOUT_TEXT = "폐기됐는지 확인하지 못했어요. 목록을 새로 고쳐 확인해 주세요";

// ---------------------------------------------------------------------------
// 타입 — D9 계약 원문 그대로
// ---------------------------------------------------------------------------

export type AccessCodeScope = "org" | "all";

export interface AdminOrganization {
  id: string;
  name: string;
  createdAt: string;
  /** 그 기관의 활성(미폐기) org 코드 수. all 코드는 어느 기관에도 세지 않는다 */
  activeCodeCount: number;
}

export interface AdminAccessCode {
  id: string;
  scope: AccessCodeScope;
  /** org면 기관, all이면 null */
  organization: { id: string; name: string } | null;
  label: string | null;
  createdAt: string;
  /** API 발급이면 actor 이메일, CLI면 'cli', 기존 행은 null */
  createdBy: string | null;
  revokedAt: string | null;
  revokedBy: string | null;
  /**
   * 원문(code_enc)이 보관돼 있어 재열람할 수 있는가(D7a). true → [복사]·[보기], false → 「원문 없음 · 다시 발급 필요」.
   * 005 이전에 발급된 기존 행은 원문이 없어 false로 온다. 폐기된 코드도 원문이 남아 있으면 true다(대조용).
   */
  rawAvailable: boolean;
}

export interface IssueAccessCodeBody {
  scope: AccessCodeScope;
  /** scope='org' + 기존 기관 */
  organizationId?: string;
  /** scope='org' + 새 기관 (name 비우면 서버가 id를 쓴다) */
  newOrganization?: { id: string; name?: string };
  label: string;
}

export interface IssueAccessCodeResult {
  accessCode: AdminAccessCode;
  /** 원문 `XXXX-XXXX-XXXX` — 한 번만 온다. 저장·로그 금지 */
  code: string;
}

export interface RevokeAccessCodeResult {
  accessCode: AdminAccessCode;
  /** 그 코드로 열려 있다가 함께 끊긴 세션 수 */
  revokedSessionCount: number;
}

export interface RevealAccessCodeResult {
  /** 원문 `XXXX-XXXX-XXXX` — reveal 때마다 돌봄이 복호화해 돌려준다. 저장·로그 금지(클립보드·행 펼침에만) */
  code: string;
}

// ---------------------------------------------------------------------------
// 호출 래퍼 — 선례 unwrap/toPartnerAdminError 재사용 + K-MRS 두 갈래
// ---------------------------------------------------------------------------

/** 404 + 본문에 `error` 필드 없음(옛 PhAI 백엔드, D12). */
const isNotReady = (err: unknown): boolean => {
  const e = err as { response?: { status?: number; data?: unknown } };
  if (e?.response?.status !== 404) return false;
  const data = e.response.data;
  return !(!!data && typeof data === "object" && "error" in (data as object));
};

const toKmrsError = (err: unknown, fallback: string, timeoutMessage?: string): PartnerAdminError => {
  if (err instanceof PartnerAdminError) return err;
  if (isNotReady(err)) {
    return new PartnerAdminError({ message: NOT_READY_TEXT, status: 404, code: null });
  }
  if (timeoutMessage && (err as { code?: string })?.code === "ECONNABORTED") {
    return new PartnerAdminError({ message: timeoutMessage, status: null, code: null });
  }
  return toPartnerAdminError(err, fallback);
};

/**
 * `call`과 같되 K-MRS 오류 두 갈래를 먼저 본다. `timeoutMessage`를 넘기면(발급·폐기) 클라이언트
 * 타임아웃에 D10 문구를, 생략하면(조회) 선례 범용 ECONNABORTED 문구를 쓴다.
 */
const kmrsCall = async <T>(
  request: () => Promise<AxiosResponse<Envelope<T>>>,
  fallback: string,
  timeoutMessage?: string
): Promise<T> => {
  let res: AxiosResponse<Envelope<T>>;
  try {
    res = await request();
  } catch (err) {
    throw toKmrsError(err, fallback, timeoutMessage);
  }
  return unwrap(res?.data, fallback);
};

// ---------------------------------------------------------------------------
// 엔드포인트 — D10 라우트
// ---------------------------------------------------------------------------

/** GET /api/v1/admin/kmrs/organizations */
export const listKmrsOrganizations = (): Promise<{ items: AdminOrganization[] }> =>
  kmrsCall(() => axiosInstance.get(`${BASE}/organizations`), "기관 목록을 불러오지 못했어요.");

/** GET /api/v1/admin/kmrs/access-codes?includeRevoked= — 화면은 항상 includeRevoked=true로 한 번 받는다 */
export const listKmrsAccessCodes = (includeRevoked = true): Promise<{ items: AdminAccessCode[] }> =>
  kmrsCall(
    () => axiosInstance.get(`${BASE}/access-codes`, { params: { includeRevoked } }),
    "접근코드 목록을 불러오지 못했어요."
  );

/** POST /api/v1/admin/kmrs/access-codes — 발급. 응답 본문에만 원문 `code`가 온다 */
export const issueKmrsAccessCode = (body: IssueAccessCodeBody): Promise<IssueAccessCodeResult> =>
  kmrsCall(
    () => axiosInstance.post(`${BASE}/access-codes`, body, { timeout: MUTATION_TIMEOUT_MS }),
    "접근코드를 발급하지 못했어요.",
    ISSUE_TIMEOUT_TEXT
  );

/** POST /api/v1/admin/kmrs/access-codes/:id/revoke — 폐기. 본문 `{}` */
export const revokeKmrsAccessCode = (id: string): Promise<RevokeAccessCodeResult> =>
  kmrsCall(
    () => axiosInstance.post(`${BASE}/access-codes/${encodeURIComponent(id)}/revoke`, {}, { timeout: MUTATION_TIMEOUT_MS }),
    "접근코드를 폐기하지 못했어요.",
    REVOKE_TIMEOUT_TEXT
  );

/**
 * POST /api/v1/admin/kmrs/access-codes/:id/reveal — 원문 재열람(D7a). 본문 `{}`.
 * 응답 원문 `code`는 클립보드·행 펼침에만 쓰고 저장·로그하지 않는다. 열람(조회)이라 폐기·발급과 달리
 * 클라이언트 타임아웃은 선례 범용 문구를 쓴다(D10 발급/폐기 전용 문구 아님). 타임아웃만 15초로 넉넉히 둔다.
 * 원문 없음은 409 `ACCESS_CODE_RAW_UNAVAILABLE`(응답 message를 그대로 토스트), 없는 id는 404다.
 */
export const revealKmrsAccessCode = (id: string): Promise<RevealAccessCodeResult> =>
  kmrsCall(
    () => axiosInstance.post(`${BASE}/access-codes/${encodeURIComponent(id)}/reveal`, {}, { timeout: MUTATION_TIMEOUT_MS }),
    "원문을 가져오지 못했어요."
  );
